import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Button, CircularProgress, Grid, MenuItem } from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { BID_STATUS } from "../../util/bid/bidConstants";
import {
  fetchEmployeeData,
  formatChoicesDataForPDF,
  formatDataForAwardPDF,
  generateFilenameForAdminAwardPDFExport,
  generateFilenameForChoicesPDFExport,
  generateFilenameForEmployeeAwardPDFExport,
} from "../../util/bid/timeOffBidPDFExportUtils";
import { isObjectEmpty } from "../../util/commonUtils";
import { ADMIN_PDF_HEADER, EMPLOYEE_PDF_HEADER } from "../../util/constants";

const IAMDATBidPDFExport = (props) => {
  const [exportLoading, setExportLoading] = useState(false);

  let columns = [
    { title: "Choice", dataKey: "choice" },
    { title: "Date", dataKey: "date" },
  ];

  const downloadBidChoicesPdf = async () => {
    setExportLoading(true);
    const doc = new jsPDF("p", "mm");
    const totalPagesExp = "{total_pages_count_string}";
    const employeeData = await fetchEmployeeData(props.bidChoiceData.eid, setExportLoading);
    const pdfData = formatChoicesDataForPDF(props.bidChoiceData.choices, props.bidChoiceData.bidType);
    const fileName = generateFilenameForChoicesPDFExport(props.bidChoiceData);
    const datCount = props.bidChoiceData.datCount || 0;

    doc.autoTable({
      theme: "grid",
      bodyStyles: { lineColor: "#e0e0e0", fontSize: 8 },
      styles: { fontSize: 10, cellPadding: { top: 1, right: 2, bottom: 0, left: 2, lineWidth: 0 } },
      headStyles: {
        fillColor: "#e0e0e0",
        textColor: "#000000",
        lineColor: "#b5b2b1",
        lineWidth: 0.1,
        fontSize: 8,
      },
      columnStyles: {
        choice: {
          cellWidth: 15,
        },
      },
      columns,
      body: pdfData.map((row) => Object.values(row)),
      margin: { top: 42 },
      didDrawPage: function (data) {
        // Header
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.setTextColor(40);
        doc.text(
          `${props.bidChoiceData.bidType}: ${props.bidChoiceData.station} | ${
            props.bidChoiceData.workgroup
          } | ${props.bidChoiceData.schedMonth.substr(0, 3)} ${props.bidChoiceData.schedYear}`,
          14,
          14
        );

        doc.setFillColor("#e0e0e0");
        doc.rect(14, 20, 182, 18, "F");
        doc.setFontSize(10);
        doc.setTextColor("#1d1d1d");
        doc.text(`${employeeData?.lastName}, ${employeeData?.shortName}  (${props.bidChoiceData?.eid})`, 17, 27);

        doc.setTextColor("#5a5a5a");
        doc.text("DATs REQUESTED: ", 17, 35);
        doc.setTextColor("#1d1d1d");
        doc.text(`${datCount}`, doc.internal.pageSize.getWidth() - 157, 35);

        doc.setTextColor("#5a5a5a");
        doc.text("CHOICES ENTERED: ", 60, 35);
        doc.setTextColor("#1d1d1d");
        doc.text(`${pdfData.length}`, doc.internal.pageSize.getWidth() - 113, 35);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);

        //footer
        var currentpage = 0;
        if (currentpage < data.pageCount) {
          doc.text(
            data.pageCount + " of " + totalPagesExp,
            doc.internal.pageSize.getWidth() - 21,
            doc.internal.pageSize.height - 10
          );

          currentpage = data.pageCount;
        }
      },
    });
    if (typeof doc.putTotalPages === "function") {
      doc.putTotalPages(totalPagesExp);
    }
    setExportLoading(false);
    doc.save(fileName);
  };

  const handleChoicesExportClick = () => {
    try {
      downloadBidChoicesPdf();
    } catch (e) {
      console.log("Error during downloading pdf!", e);
    }
  };

  const renderChoicesExportButton = () => {
    return (
      <Button
        variant={props.buttonVariant}
        startIcon={
          !exportLoading ? (
            <UploadFileIcon />
          ) : (
            <CircularProgress size={20} color={props.buttonVariant === "outlined" ? "primary" : "secondary"} />
          )
        }
        onClick={() => {
          handleChoicesExportClick();
        }}
      >
        EXPORT
      </Button>
    );
  };

  const downloadAwardPdf = (pdfType, pdfData, columnList, fileName) => {
    const doc = new jsPDF("p", "mm");
    const isAdmin = pdfType === ADMIN_PDF_HEADER;

    const adminColumns = columnList;
    let exclusionList = ["awardType", "awardedRank"];
    const employeeColumns = columnList.filter((item) => !exclusionList.includes(item.dataKey));

    let finalY = 20; // Start at 20mm from the top

    const addFooters = function (doc) {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.getWidth() - 25, doc.internal.pageSize.height - 10);
      }
    };

    const addTitle = function (doc, title, y) {
      doc.setFillColor("#e0e0e0");
      doc.rect(14, y, 182, 6, "F");
      doc.setFontSize(10);
      doc.setTextColor("#5a5a5a");
      doc.text(title, 15, y + 4);
      if (isAdmin) {
        let displayedCalculatedDailyAllotment;
        displayedCalculatedDailyAllotment = isObjectEmpty(props.calcDailyAllotments)
          ? "NA"
          : `Calculated Daily Allotment: ${props.calcDailyAllotments.amOrPmCount} AM OR PM`;
        doc.text(displayedCalculatedDailyAllotment, 125, y + 4);
      }
      return y + 8; // Return the Y position after the title
    };

    const createTable = function (doc, title, data, startY) {
      const columns = isAdmin
        ? adminColumns?.map((col) => ({ ...col, dataKey: col.field }))
        : employeeColumns?.map((col) => ({ ...col, dataKey: col.field }));

      try {
        doc.autoTable({
          theme: "grid",
          bodyStyles: { lineColor: "#e0e0e0", fontSize: 7 },
          styles: { fontSize: 10, cellPadding: { top: 1, right: 1, bottom: 0, left: 1, lineWidth: 0 } },
          headStyles: {
            fillColor: "#e0e0e0",
            textColor: "#000000",
            lineColor: "#b5b2b1",
            lineWidth: 0.1,
            fontSize: 8,
          },
          columns: columns,
          body: data.map((row) => Object.values(row)),
          startY: startY + 6,
          margin: { top: 26 },
          rowPageBreak: "avoid",
          didDrawPage: function (data) {
            // Header
            doc.setFont("helvetica", "bold");
            doc.setFontSize(15);
            doc.setTextColor(40);
            doc.text(
              `${props.bidChoiceData.bidType}: ${props.bidChoiceData.station} | ${
                props.bidChoiceData.workgroup
              } | ${props.bidChoiceData.schedMonth.substr(0, 3)} ${props.bidChoiceData.schedYear}`,
              14,
              14
            );

            if (isAdmin) {
              data.settings.startY = addTitle(doc, title, 20);
            }
          },
        });
      } catch (error) {
        console.error(`Error in ${title} table:`, error);
      }

      return doc.lastAutoTable.finalY;
    };

    createTable(doc, "", pdfData, finalY);

    addFooters(doc);
    doc.save(fileName);
  };

  const handleAwardExportClick = (pdfType) => {
    try {
      let pdfData, fileName;
      const columnList = [
        { title: "Date", dataKey: "date" },
        { title: "Time Off Type", dataKey: "awardedTimeOffType" },
        { title: "EID", dataKey: "eid" },
        { title: "Name", dataKey: "name" },
        { title: "Bid Group\nRank", dataKey: "awardedRank" },
        { title: "Award Type", dataKey: "awardType" },
      ];
      if (pdfType === ADMIN_PDF_HEADER) {
        pdfData = formatDataForAwardPDF(props.bidAllotments, pdfType);
        fileName = generateFilenameForAdminAwardPDFExport(props.bidChoiceData);
      } else if (pdfType === EMPLOYEE_PDF_HEADER) {
        pdfData = formatDataForAwardPDF(props.bidAllotments, pdfType);
        fileName = generateFilenameForEmployeeAwardPDFExport(props.bidChoiceData);
      }
      downloadAwardPdf(pdfType, pdfData, columnList, fileName);
      props.onExportMenuClose();
    } catch (e) {
      console.log("Error during downloading pdf!", e);
    }
  };

  const renderAdminAwardExportButtons = () => {
    return (
      <>
        <MenuItem
          disabled={props.bidChoiceData.status !== BID_STATUS.AWARDED}
          onClick={() => {
            handleAwardExportClick(ADMIN_PDF_HEADER);
          }}
        >
          Admin PDF
        </MenuItem>
        <MenuItem
          disabled={props.bidChoiceData.status !== BID_STATUS.AWARDED}
          onClick={() => {
            handleAwardExportClick(EMPLOYEE_PDF_HEADER);
          }}
        >
          Employee View PDF
        </MenuItem>
      </>
    );
  };

  const renderEmployeeAwardExportButtons = () => {
    return (
      <Grid container justifyContent={"center"} sx={{ marginTop: "20px" }}>
        <Grid>
          <Button
            component="button"
            variant="contained"
            color="primary"
            onClick={() => {
              handleAwardExportClick(EMPLOYEE_PDF_HEADER);
            }}
            disabled={props.isMyBidResultsDialogLoading}
          >
            EXPORT BID RESULTS (PDF)
          </Button>
        </Grid>
        <Grid style={{ marginTop: "10px" }}>
          <Button
            component="button"
            variant="contained"
            color="primary"
            onClick={() => {
              handleChoicesExportClick();
            }}
            disabled={props.isMyBidResultsDialogLoading}
          >
            EXPORT MY CHOICES (PDF)
          </Button>
        </Grid>
      </Grid>
    );
  };

  const renderExportButtons = () => {
    if (props.isAdminAwardExport) {
      return renderAdminAwardExportButtons();
    } else if (props.isEmployeeAwardExport) {
      return renderEmployeeAwardExportButtons();
    } else {
      return renderChoicesExportButton();
    }
  };

  return <>{renderExportButtons()}</>;
};

IAMDATBidPDFExport.propTypes = {
  bidChoiceData: PropTypes.object.isRequired,
  buttonVariant: PropTypes.string,
  bidAllotments: PropTypes.array,
  isAdminAwardExport: PropTypes.bool,
  isEmployeeAwardExport: PropTypes.bool,
  onExportMenuClose: PropTypes.func,
  calcDailyAllotments: PropTypes.object,
  isMyBidResultsDialogLoading: PropTypes.bool,
};

export default IAMDATBidPDFExport;
